/**
 * Personal Mode Export Limit Message
 *
 * Shows a dialog when Personal mode restricts export due to large row count.
 * Explains the limit and provides upgrade option.
 */

import { AlertTriangle, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { PERSONAL_MODE_MAX_EXPORT_ROWS } from '../utils/personalModeLimits';

type PersonalModeExportLimitMessageProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rowCount: number;
  onUpgrade: () => void;
};

/**
 * Dialog shown when export exceeds Personal mode row limits.
 * Offers upgrade prompt.
 */
export function PersonalModeExportLimitMessage({
  open,
  onOpenChange,
  rowCount,
  onUpgrade,
}: PersonalModeExportLimitMessageProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="personal-mode-export-limit">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            Export Limit Reached
          </DialogTitle>
          <DialogDescription>
            Your export contains {rowCount.toLocaleString()} rows, which exceeds the Personal mode limit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Limit explanation */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <p className="text-sm text-foreground mb-2">
              Personal mode limits exports to {PERSONAL_MODE_MAX_EXPORT_ROWS.toLocaleString()} rows.
            </p>
            <p className="text-sm text-muted-foreground">
              Consider filtering your data to reduce the row count, or upgrade to Pro for unlimited exports.
            </p>
          </div>

          {/* Upgrade prompt */}
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                Upgrade to Pro
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Export data of any size without limits.
            </p>
            <Button onClick={onUpgrade} className="gap-2 w-full" data-testid="upgrade-button">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>

          {/* Cancel button */}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
            data-testid="cancel-button"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
